import slice from 'lodash/slice';
import stringify from 'safe-stable-stringify';
import {
    derived,
    get,
    type Readable,
    readable,
    writable,
    type Writable,
} from 'svelte/store';
import * as z from 'zod';
import { query } from '$lib/apollo/SvelteApollo';
import type { ShareableContainer } from '$lib/container/shareableContainer';
import singleton from '$lib/utils/factory';
import { keep } from '$lib/utils/filterNotNull';
import {
    createSessionBoolStorable,
    createSessionStorable,
} from '$lib/utils/store/storable';
import { innerWidthStore } from '$lib/utils/store/windowStore';
import { stripUndefinedValues } from '$lib/utils/stripUndefinedValues';
import type { lazy } from '$lib/utils/types';
import {
    InboxNbUnreadThreadsDocument,
    type CurrentParticipantInput,
    type ParticipantInput,
    type ThreadListPageQueryVariables,
} from '$gql/graphql';
import { ThreadListStore } from '$components/Inbox/ThreadListStore';

export class InboxStore {
    constructor(
        private readonly apollo: ShareableContainer['apollo'],
        private readonly browser: ShareableContainer['browser'],
        private readonly mercureManager: ShareableContainer['mercureManager'],
    ) {}

    nbUnreadThreadsStore = singleton(() => {
        const initial = this.apollo().cache.readQuery({
            query: InboxNbUnreadThreadsDocument,
            returnPartialData: false,
        });

        return readable(initial?.inboxNbUnreadThreads ?? 0, (set) => {
            const { result, refetch } = query(
                InboxNbUnreadThreadsDocument,
                {},
                {
                    apollo: this.apollo,
                    browser: this.browser,
                },
            );

            const unsubs = [
                this.mercureManager().onPrivateEvent('inbox/new-message', (_event) => {
                    refetch().catch(console.error);
                }),

                this.mercureManager().onPrivateEvent(
                    'inbox/message-mark-read',
                    (_event) => {
                        refetch().catch(console.error);
                    },
                ),
                result.subscribe(($result) => {
                    set($result.data?.inboxNbUnreadThreads ?? 0);
                }),
            ];

            return () => {
                unsubs.forEach((r) => r());
            };
        });
    });

    currentParticipant: lazy<Writable<CurrentParticipantInput | null>> = singleton(() =>
        writable(null),
    );

    openedThreadsInInboxBar = singleton(() => {
        const key = derived(
            this.currentParticipant(),
            ($participant, set) => {
                if ($participant == null) {
                    set(`GoAfricaNext-InboxBar-openedThreads-null`);
                } else {
                    set(
                        `GoAfricaNext-InboxBar-openedThreads-${stringify(stripUndefinedValues($participant))}`,
                    );
                }
            },
            'GoAfricaNext-InboxBar-openedThreads-null',
        );

        return createSessionStorable<number[], number[]>({
            key,
            isBrowser: this.browser(),
            defaultData: [],
            zodSchema: z.array(z.number()),
            encode: (a) => a,
            decode: (a) => a,
        });
    });

    canDisplayInboxBar: lazy<Readable<boolean>> = singleton(() => {
        const innerWidth = innerWidthStore({ browser: this.browser(), defaultValue: 0 });

        return derived(innerWidth, ($innerWidth, set) => {
            set($innerWidth >= 1024);
        });
    });

    openThreadInInboxBar(this: this, threadId: number) {
        const openedThreads = this.openedThreadsInInboxBar();
        if (!get(openedThreads).includes(threadId)) {
            openedThreads.update(($openedThreads) =>
                slice([threadId, ...$openedThreads], 0, 3),
            );
        }
    }

    closeThreadInInboxBar(this: this, threadId: number) {
        const openedThreads = this.openedThreadsInInboxBar();
        openedThreads.update(($openedThreads) =>
            keep($openedThreads, (tId) => tId !== threadId),
        );
    }

    createThreadStore: lazy<{
        open: Writable<boolean>;
        defaultParticipants: Writable<ParticipantInput[]>;
        relatedJobOfferId: Writable<number | null>;
    }> = singleton(() => ({
        open: createSessionBoolStorable({
            key: readable(`GoAfricaNext-InboxBar-createThreadIsOpened`),
            isBrowser: this.browser(),
            defaultData: false,
        }),
        defaultParticipants: writable([]),
        relatedJobOfferId: writable(null),
    }));

    openCreateThreadInInboxBar(
        this: this,
        args: {
            defaultParticipants: ParticipantInput[];
            relatedJobOfferId?: number | null;
        },
    ) {
        this.createThreadStore().open.set(false);
        this.createThreadStore().defaultParticipants.set([]);
        this.createThreadStore().defaultParticipants.set(args.defaultParticipants);
        this.createThreadStore().relatedJobOfferId.set(null);
        this.createThreadStore().relatedJobOfferId.set(args.relatedJobOfferId ?? null);
        this.createThreadStore().open.set(true);
    }

    closeCreateThreadInInboxBar(this: this) {
        this.createThreadStore().open.set(false);
        this.createThreadStore().defaultParticipants.set([]);
    }

    queryHighlight: lazy<Writable<string>> = singleton(() => {
        return writable('');
    });

    createThreadListStore({
        initialVariables,
    }: {
        initialVariables: Omit<ThreadListPageQueryVariables, 'page'>;
    }): ThreadListStore {
        return new ThreadListStore({
            mercureManager: () => this.mercureManager(),
            apollo: () => this.apollo(),
            browser: () => this.browser(),
            initialVariables: initialVariables,
        });
    }
}
